"use client";

import { useEffect, useRef, useState } from "react";
import { openConsentSettings, useOptionalScriptsAllowed } from "@/lib/consent";

/* Real live Facebook Page feed (Page Plugin) — the authentic social proof a custom wall
 * can't fake. Lazy-loaded: the FB JS SDK only loads when the section scrolls near view,
 * so it costs zero up-front page speed. Renders nothing unless a Page URL is configured
 * (NEXT_PUBLIC_FB_PAGE_URL) — safe to ship before the Page is connected. */

type FBSdk = { XFBML?: { parse: () => void } };
declare global {
  interface Window {
    FB?: FBSdk;
  }
}

export function FacebookFeed({ pageUrl }: { pageUrl?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  /* The Facebook SDK is a third-party tracker: it loads only after an explicit Accept. */
  const allowed = useOptionalScriptsAllowed();

  // Reveal (and load the SDK) only when scrolled near view.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e?.isIntersecting) {
          setShow(true);
          obs.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!show || !allowed) return;
    if (document.getElementById("fb-sdk")) {
      window.FB?.XFBML?.parse();
      return;
    }
    const s = document.createElement("script");
    s.id = "fb-sdk";
    s.async = true;
    s.defer = true;
    s.crossOrigin = "anonymous";
    s.src = "https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v23.0";
    document.body.appendChild(s);
  }, [show, allowed]);

  if (!pageUrl) return null; // Page not connected yet → render nothing (no broken box)

  return (
    <div ref={ref} className="mt-12">
      {show && !allowed && (
        <p className="rounded-xl border border-white/20 p-4 text-[14px] text-white">
          Our Facebook feed is hidden until you allow optional third-party content.{" "}
          <button type="button" onClick={openConsentSettings} className="underline underline-offset-2">
            Change cookie settings
          </button>
        </p>
      )}
      {show && allowed && (
        <>
          <div id="fb-root" />
          <div
            className="fb-page"
            data-href={pageUrl}
            data-tabs="timeline"
            data-width="500"
            data-height="700"
            data-hide-cover="false"
            data-show-facepile="true"
          />
        </>
      )}
    </div>
  );
}
